import { useState, useCallback } from "react";
import { APIService, getCarrierName } from "../utilities/Constants";
import { apiGet } from "./utils/apiUtils";
import { ErrorHandler } from "./utils/errorUtils";
import { messages } from "../../src/resources/strings";
import {
  toPoliciesModal,
  toPolicyDetail,
} from "../hooks/policyScreen/policyParser";

interface PolicyResponse {
  response?: any;
  errorCode: any;
  message?: string;
  error?: string;
}

export const usePolicies = () => {
  const [loading, setLoading] = useState(false);

  const customErrorHandlers: ErrorHandler[] = [
    {
      case: 403,
      onError: () => ({
        errorCode: messages.error.noPolicyFound,
        message: "No policies available for this user",
      }),
    },
    {
      case: 401,
      onError: () => ({
        errorCode: messages.error.noHarmonyUserFound,
        message: "User not authorized for Harmony",
      }),
    },
  ];

  const getPolicies = useCallback(async (): Promise<PolicyResponse> => {
    setLoading(true);
    try {
      const response = await apiGet<any>(
        APIService.harmony,
        "/policies/list",
        { isUserAuthRequired: true },
        { policySource: getCarrierName() },
        customErrorHandlers
      );

      if (response?.result?.length === 0) {
        return {
          errorCode: messages.error.noPolicyFound,
          message: "No policies found",
        };
      } else if (response?.result?.length) {
        let sortedResponse = toPoliciesModal(response?.result);
        return {
          response: sortedResponse,
          errorCode: messages.error.noError,
        };
      }
      return response;
    } finally {
    }
  }, []);

  const getDiscoverPolicies = useCallback(async (): Promise<PolicyResponse> => {
    setLoading(true);
    try {
      const response = await apiGet<any>(
        APIService.harmony,
        "/policies/discover",
        { isUserAuthRequired: true, isSignalAbortAllowed:true },
        { policySource: getCarrierName() },
        customErrorHandlers
      );

      if (response?.result?.length === 0) {
        return { errorCode: messages.error.noPolicyFound };
      } else if (response?.result?.length) {
        let sortedResponse = toPoliciesModal(response?.result);

        return {
          response: sortedResponse,
          errorCode: messages.error.noError,
        };
      }
      return response;
    } finally {
    }
  }, []);

  const getPolicyDetail = useCallback(
    async (
      policyNumber: string
    ): Promise<{
      errorCode: string;
      response: any;
    }> => {
      setLoading(false);
      try {
        const response = await apiGet<any>(
          APIService.harmony,
          `/policy/${policyNumber}`,
          { isUserAuthRequired: true },
          { policySource: getCarrierName() },
          customErrorHandlers
        );

        if (!response?.result) {
          return {
            errorCode: response?.message.toString(),
            response: response.result,
          };
        } else if (response?.result) {
          return {
            response: toPolicyDetail(response?.result),
            errorCode: messages.error.noError,
          };
        }
        return response;
      } finally {
      }
    },
    []
  );

  return {
    getPolicies,
    getPolicyDetail,
    getDiscoverPolicies,
    loading,
  };
};
